import { useLight } from "@Light/services/light";
import { RateGroup } from "@Light/services/lightTypes";
import type { Account } from "@Light/components/page/account";

const ACTIVE = "active";

export type EnrollmentRateGroups = {
  requestedGroup?: RateGroup;
  effectiveGroup: RateGroup;
};

export function findEnrollmentRateGroups(
  rateGroups: RateGroup[],
  plan?: string,
): EnrollmentRateGroups {
  const uuidToRateGroup = new Map(rateGroups.map((g) => [g.uuid, g]));
  const requestedGroup = plan ? uuidToRateGroup.get(plan) : undefined;
  const defaultPlan = rateGroups.find((g) => g.is_default_for_app);
  const firstPlan = rateGroups[0];

  if (!firstPlan) {
    throw new Error("No rate groups available.");
  }

  if (!requestedGroup || requestedGroup.state === ACTIVE) {
    return {
      requestedGroup,
      effectiveGroup: requestedGroup ?? defaultPlan ?? firstPlan,
    };
  }

  const renewalPlan = requestedGroup.renewal_rate_group_uuid
    ? uuidToRateGroup.get(requestedGroup.renewal_rate_group_uuid)
    : undefined;

  return {
    requestedGroup,
    effectiveGroup: renewalPlan ?? defaultPlan ?? firstPlan,
  };
}

/**
 * Returns both the requested rate group (from the enrollment link) and the
 * effective rate group (the renewal plan if the requested one is inactive).
 */
export function useEnrollmentRateGroups(
  plan?: string,
): EnrollmentRateGroups | undefined {
  const { useRateGroupsQuery } = useLight();
  const rateGroups = useRateGroupsQuery();

  if (!rateGroups.isSuccess) {
    return undefined;
  }
  return findEnrollmentRateGroups(rateGroups.data, plan);
}

export function findRateGroup(
  rateGroups: RateGroup[],
  plan?: string,
): RateGroup | undefined {
  return findEnrollmentRateGroups(rateGroups, plan).effectiveGroup;
}

export function getPlanFromDefaultLocation(
  account: Account,
): string | undefined {
  const location = account.locations[0];
  if (location?.plan_uuid) {
    return location.plan_uuid;
  }
  return undefined;
}

/**
 * This hook fetches the rate groups and returns the rate group for the given plan.
 */
export function useRateGroup(plan?: string): RateGroup | undefined {
  const { useRateGroupsQuery, endpoints } = useLight();
  const rateGroups = useRateGroupsQuery();

  const { data: account } = endpoints.getAccount.useQueryState();

  if (account) {
    const locationPlan = getPlanFromDefaultLocation(account);
    if (locationPlan) {
      plan = locationPlan;
    }
  }

  if (!rateGroups.isSuccess) {
    return undefined;
  }
  return findRateGroup(rateGroups.data, plan);
}

/**
 * This hook only observes the rate groups, it won't trigger a fetch.
 */
export function useObservedRateGroup(plan?: string): RateGroup | undefined {
  const light = useLight();
  const { data: rateGroups } = light.endpoints.rateGroups.useQueryState();

  const { data: account } = light.endpoints.getAccount.useQueryState();

  if (!rateGroups) {
    return undefined;
  }

  if (account) {
    const locationPlan = getPlanFromDefaultLocation(account);
    if (locationPlan) {
      plan = locationPlan;
    }
  }

  return findRateGroup(rateGroups, plan);
}
